/**
 * useGroupHeaderSettings
 *
 * Company-level setting that controls which columns are shown in the
 * collapsed group header row on the Projects list view.
 *
 * Up to 3 column IDs can be selected. The labels shown are always
 * pulled from the live column definitions so they stay in sync.
 *
 * Persisted in localStorage under 'group-header-settings'.
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'group-header-settings';
const MAX_COLUMNS = 3;

/** All column IDs that can be subtotalled — must match projects-grid.tsx */
export const SUBTOTALABLE_COLUMN_IDS = [
  'budget',
  'actual-sales',
  'sales-variance',
  'cost-budget',
  'actual-cost',
  'cost-variance',
  'gross-profit',
  'wip-amount',
  'forecast-at-completion',
  'allocated-hours',
  'actual-hours',
  'hours-variance',
] as const;

export type SubtotalableColumnId = (typeof SUBTOTALABLE_COLUMN_IDS)[number];

/** Default columns shown — sales-focused but sensible for most customers */
const DEFAULT_COLUMNS: SubtotalableColumnId[] = [
  'budget',
  'actual-sales',
  'gross-profit',
];

function load(): SubtotalableColumnId[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(raw) as unknown;
    if (
      Array.isArray(parsed) &&
      parsed.every((v) => SUBTOTALABLE_COLUMN_IDS.includes(v as SubtotalableColumnId))
    ) {
      return parsed as SubtotalableColumnId[];
    }
  } catch {
    // ignore
  }
  return DEFAULT_COLUMNS;
}

function save(cols: SubtotalableColumnId[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
}

export interface GroupHeaderSettings {
  /** The currently saved column IDs (up to 3) */
  selectedColumnIds: SubtotalableColumnId[];
  /** Toggle a column on/off — respects the MAX_COLUMNS limit */
  toggleColumn: (id: SubtotalableColumnId) => void;
  /** Replace the whole selection */
  setColumns: (ids: SubtotalableColumnId[]) => void;
  /** Restore defaults */
  resetToDefaults: () => void;
  /** Whether the selection has been changed since last save */
  hasUnsavedChanges: boolean;
  /** Persist current selection */
  saveSettings: () => void;
  /** Discard pending changes */
  discardChanges: () => void;
}

export function useGroupHeaderSettings(): GroupHeaderSettings {
  const [saved, setSaved] = useState<SubtotalableColumnId[]>(load);
  const [pending, setPending] = useState<SubtotalableColumnId[]>(load);

  const hasUnsavedChanges =
    JSON.stringify(pending) !== JSON.stringify(saved);

  const toggleColumn = useCallback((id: SubtotalableColumnId) => {
    setPending((prev) => {
      if (prev.includes(id)) {
        return prev.filter((c) => c !== id);
      }
      if (prev.length >= MAX_COLUMNS) return prev; // already at limit
      return [...prev, id];
    });
  }, []);

  const setColumns = useCallback((ids: SubtotalableColumnId[]) => {
    setPending(ids.slice(0, MAX_COLUMNS) as SubtotalableColumnId[]);
  }, []);

  const resetToDefaults = useCallback(() => {
    setPending([...DEFAULT_COLUMNS]);
  }, []);

  const saveSettings = useCallback(() => {
    save(pending);
    setSaved([...pending]);
  }, [pending]);

  const discardChanges = useCallback(() => {
    setPending([...saved]);
  }, [saved]);

  return {
    selectedColumnIds: pending,
    toggleColumn,
    setColumns,
    resetToDefaults,
    hasUnsavedChanges,
    saveSettings,
    discardChanges,
  };
}

/** Read-only helper for consuming the saved setting outside React */
export function getGroupHeaderColumns(): SubtotalableColumnId[] {
  return load();
}
